import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import SearchInput from "@/components/SearchInput";
import { useDistrict } from "@/lib/district-context";
import { useCoordinatorChurchName } from "@/hooks/useCoordinatorChurchName";
import { useWeeklyPlan } from "@/lib/weekly-plan";
import { FieldAgenda, LocalAgenda, PersonalAgenda, AgendaHero, AgendaTabs } from "@/features/agenda/components";
import PageWrapper from "@/components/layout/PageWrapper";
import TopBar from "@/components/TopBar";
import { useRegisterKeviaContext } from "../contexts/KeviaContext";

export default function Agenda() {
  const { district, isCoordinator, userProfile } = useDistrict();
  const coordinatorChurchName = useCoordinatorChurchName();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { getByGetDay } = useWeeklyPlan();

  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [tab, setTab] = useState(() => {
    // Coordinators only have access to their church agenda.
    if (isCoordinator) return "local";
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) return tabFromUrl;
    try {
      return sessionStorage.getItem("agenda.tab") || "personal";
    } catch {
      return "personal";
    }
  });

  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debounceRef = useRef(null);

  const handleSearchChange = (value) => {
    setSearchText(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(value.trim()), 250);
  };
  const clearSearch = () => {
    setSearchText("");
    setSearchQuery("");
  };

  useEffect(() => {
    clearSearch();
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabFromUrl !== tab) setTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (k) => {
    setTab(k);
    setSearchParams({ tab: k }, { replace: true });
    try { sessionStorage.setItem("agenda.tab", k); } catch { /* ignore */ }
  };

  const today = new Date();
  const todayActivity = getByGetDay(today.getDay());

  const placeholders = {
    personal: "Buscar eventos personales…",
    local:    "Buscar eventos de la iglesia…",
    field:    "Buscar eventos del campo…",
  };

  // Kevia dynamic context — registered while this page is mounted
  const keviaCtx = useMemo(() => {
    const tabLabels = {
      personal: "Agenda personal",
      local:    "Agenda de la iglesia",
      field:    "Agenda del campo",
    };
    const activityLabel = todayActivity
      ? (todayActivity.label || todayActivity.name || todayActivity.type || String(todayActivity))
      : null;
    const lines = [
      "Módulo: Agenda",
      `  Vista activa: ${tabLabels[tab] ?? tab}`,
      activityLabel && `  Actividad de hoy: ${activityLabel}`,
    ].filter(Boolean);
    return {
      type:         "module",
      id:           `agenda-${tab}`,
      name:         "Agenda",
      tone:         "neutral",
      contextBlock: `[Contexto activo en Kéves]\n${lines.join("\n")}\n\n[Pregunta del pastor]\n`,
    };
  }, [tab, todayActivity]);
  useRegisterKeviaContext(keviaCtx);

  return (
    <PageWrapper testId="agenda-page" onRefresh={handleRefresh}>
      <TopBar title="Agenda" subtitle={isCoordinator ? (coordinatorChurchName || undefined) : district?.name} safeFallback="/district" />

      {/* Search bar */}
      <div className="px-4 pt-3 pb-1">
        <SearchInput
          value={searchText}
          onChange={handleSearchChange}
          placeholder={placeholders[tab]}
        />
      </div>

      <AgendaHero
        districtName={isCoordinator ? (coordinatorChurchName || district?.name) : district?.name}
        todayActivity={todayActivity}
        isCoordinator={isCoordinator}
      />

      {/* Coordinators only see their church agenda — no tab switcher needed */}
      {!isCoordinator && (
        <div className="px-4 pt-4">
          <AgendaTabs tab={tab} onChange={handleTabChange} />
        </div>
      )}

      <div className="px-4 pt-3 pb-6">
        {tab === "personal" && !isCoordinator && (
          <PersonalAgenda
            districtId={district?.id}
            searchQuery={searchQuery}
            key={`personal-${location.key}-${refreshKey}`}
          />
        )}
        {tab === "local" && (
          <LocalAgenda
            districtId={district?.id}
            defaultChurchId={isCoordinator ? userProfile?.church_id : searchParams.get("church_id")}
            lockedChurchId={isCoordinator ? userProfile?.church_id : undefined}
            searchQuery={searchQuery}
            key={`local-${location.key}-${refreshKey}`}
          />
        )}
        {tab === "field" && !isCoordinator && (
          <FieldAgenda
            districtId={district?.id}
            searchQuery={searchQuery}
            key={`field-${location.key}-${refreshKey}`}
          />
        )}
      </div>
    </PageWrapper>
  );
}
